import { css } from '@emotion/css';
import {
  Divider,
  Pagination,
  PaginationProps,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import * as React from 'react';

import { DefaultWarningIndicator } from '@/components/diagrams/components/Tile/DefaultWarningIndicator';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { COLORS } from '@/styles/tokens/colors';

import { EntityDiagram_NodeFragment } from '../types';

// Type for warning data
export interface WarningData {
  key: 'notfullydistributed' | 'overdistributed' | 'willnotreceive';
  message: string;
}

export function getNotFullyDistributedWarning(
  node: EntityDiagram_NodeFragment
): WarningData {
  const entityDisplayName = (() => {
    if (node.node?.__typename === 'Entity') {
      return node.node.subtype.displayName;
    }

    if (node.node?.__typename === 'TestamentaryEntity') {
      return node.node.displayName;
    }

    if (node.node?.__typename === 'ClientProfile') {
      return node.node.displayName;
    }

    return 'This';
  })();

  return {
    key: 'notfullydistributed',
    message: `${entityDisplayName} is not fully distributed.`,
  };
}

export function getSpecifyPrimaryBeneficiaryWarning(): WarningData {
  return {
    key: 'notfullydistributed',
    message: 'Please specify a primary beneficiary',
  };
}

export function getSpecifyContingentBeneficiaryWarning(): WarningData {
  return {
    key: 'notfullydistributed',
    message: 'Please specify a contingent beneficiary',
  };
}

export function getOverDistributedWarning(): WarningData {
  return {
    key: 'overdistributed',
    message: 'Distributions specified exceed 100%.',
  };
}

export function getWillNotReceiveDispositionsWarning(
  node: EntityDiagram_NodeFragment
): WarningData {
  const entityDisplayName = (() => {
    if (node.node?.__typename === 'Entity') {
      return node.node.subtype.displayName;
    }

    if (node.node?.__typename === 'TestamentaryEntity') {
      return node.node.displayName;
    }

    if (node.node?.__typename === 'ClientProfile') {
      return node.node.displayName;
    }

    return 'This';
  })();

  return {
    key: 'willnotreceive',
    message: `This recipient will not receive any dispositions; ${entityDisplayName} fully distributes to other recipients.`,
  };
}

const paginationClasses: NonNullable<PaginationProps['classes']> = {
  ul: css({
    // Hides the numbered page buttons so just the arrow controls are visible
    '> li:not(:first-child):not(:last-child)': {
      display: 'none',
    },
  }),
};

export function PaginatedTooltipContent({
  warnings,
}: {
  warnings: WarningData[];
}) {
  const [index, setIndex] = useState(0);
  const currentPage = index + 1;

  const childCount = warnings.length;

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    event.stopPropagation();
    setIndex(page - 1);
  };

  const handleContainerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const currentWarning = warnings[index];
  if (!currentWarning) return null;

  return (
    <Stack gap={2} onClick={handleContainerClick} width="200px">
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        height="1rem"
      >
        <Typography variant="body-xs">
          Warning {currentPage} of {childCount}
        </Typography>
        <Pagination
          size="small"
          shape="rounded"
          classes={paginationClasses}
          count={childCount}
          page={currentPage}
          onChange={handlePageChange}
        />
      </Stack>
      <Divider sx={{ borderColor: COLORS.GRAY[200] }} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr',
        }}
      >
        {warnings.map((warning, idx) => (
          <Typography
            key={warning.key}
            variant="body-xs"
            sx={{
              gridArea: '1 / 1',
              opacity: index === idx ? 1 : 0,
            }}
          >
            {warning.message}
          </Typography>
        ))}
      </div>
    </Stack>
  );
}

// Component to handle multiple warning indicators with paging
export const MultipleWarningIndicators = ({
  warnings,
}: {
  warnings: WarningData[];
}): React.ReactElement | null => {
  // Remove duplicate warnings based on key
  const uniqueWarnings = React.useMemo(() => {
    const seen = new Set<string>();
    return warnings.filter((warning) => {
      if (seen.has(warning.key)) {
        return false;
      }
      seen.add(warning.key);
      return true;
    });
  }, [warnings]);

  if (uniqueWarnings.length === 0) return null;

  if (uniqueWarnings.length === 1 && uniqueWarnings[0]) {
    return (
      <Tooltip
        sx={(theme: { zIndex: { tooltip: number } }) => ({
          zIndex: theme.zIndex.tooltip + 1,
        })}
        title={
          <Typography variant="body-xs">{uniqueWarnings[0].message}</Typography>
        }
      >
        <DefaultWarningIndicator isIconVariant />
      </Tooltip>
    );
  }

  return (
    <Tooltip
      sx={(theme: { zIndex: { tooltip: number } }) => ({
        zIndex: theme.zIndex.tooltip + 1,
      })}
      title={<PaginatedTooltipContent warnings={uniqueWarnings} />}
    >
      <DefaultWarningIndicator isIconVariant />
    </Tooltip>
  );
};
